// import { v4 as uuidV4 } from 'uuid';
import Ratio from './Ratio';
import { IMedia } from './Media';
import Observable from './Observable';

interface ISize {
  width: number,
  height: number,
}

class Video extends Observable implements IMedia {
  static EVENT = {
    ABORT: 'abort',
    CAN_PLAY: 'canplay',
    CAN_PLAY_THROUGH: 'canplaythrough',
    CAN_PLAY_THROUGH_FIRST_FRAME_READY: 'canplaythrough_first_frame_ready',
    DURATION_CHANGE: 'durationchange',
    EMPTIED: 'emptied',
    ENDED: 'ended',
    ERROR: 'error',
    LOADED_DATA: 'loadeddata',
    LOADED_META_DATA: 'loadedmetadata',
    LOAD_START: 'loadstart',
    PAUSE: 'pause',
    PLAY: 'play',
    PLAYING: 'playing',
    PROGRESS: 'progress',
    RATE_CHANGE: 'ratechange',
    SEEKED: 'seeked',
    SEEKING: 'seeking',
    STALLED: 'stalled',
    SUSPEND: 'suspend',
    TIME_UPDATE: 'timeupdate',
    VOLUME_CHANGE: 'volumechange',
    WAITING: 'waiting',
  };

  private readonly id: string;

  private readonly src: string;

  private readonly video: HTMLVideoElement;

  private readonly loadForceIn: number;

  private isRendering: boolean;

  private loadingTimeout: any;

  constructor(id: string, src: string, loadForceIn = 0) {
    super();

    this.id = id;
    this.src = src;
    this.isRendering = false;
    this.loadForceIn = loadForceIn;

    this.video = (document.querySelector(`[data-id="${id}"]`) as HTMLVideoElement | null)
      || (document.createElement('video') as HTMLVideoElement);
    this.video.setAttribute('data-id', id);
    this.video.setAttribute('webkit-playsinline', 'true');
    this.video.setAttribute('playsinline', 'true');
    this.video.style.width = '100%';
    this.video.style.height = 'auto';
    while (this.video.firstChild) {
      this.video.firstChild.remove();
    }

    this.addListeners();
  }

  public load = (): this => {
    if (!this.video) {
      return this;
    }

    const source: HTMLSourceElement = document.createElement('source');
    source.setAttribute('src', this.src);
    source.setAttribute('type', 'video/mp4');
    this.video.appendChild(source);
    this.video.load();

    return this;
  };

  public play = (): this => {
    if (this.isPlaying()) {
      return this;
    }
    this.video.play().catch((error) => {
      // eslint-disable-next-line no-console
      console.log(error);
    });
    return this;
  };

  public playAsync = (): Promise<this> => {
    if (this.isPlaying()) {
      return Promise.resolve(this);
    }
    return this.video.play().then(() => this);
  };

  public pause = (): this => {
    this.video.pause();
    return this;
  };

  public stop = (): this => {
    this.video.pause();
    this.video.currentTime = 0;
    return this;
  };

  public mute = (): this => {
    this.video.muted = true;
    return this;
  };

  public unMute = (): this => {
    this.video.muted = false;
    return this;
  };

  public volume = (value: number): this => {
    this.video.volume = value;
    return this;
  };

  public loop = (value: boolean): this => {
    this.video.loop = value;
    const hasLoopAttr = this.video.hasAttribute('loop');
    if (value && !hasLoopAttr) {
      this.video.setAttribute('loop', 'loop');
    }
    if (!value && hasLoopAttr) {
      this.video.removeAttribute('loop');
    }
    return this;
  };

  public getSize = (): ISize => {
    const { videoWidth, videoHeight } = this.video;
    return ({
      width: Number(videoWidth),
      height: Number(videoHeight),
    });
  };

  public getRatio = (): Ratio => {
    const { videoWidth, videoHeight } = this.video;
    return new Ratio(videoWidth, videoHeight);
  };

  public getElement = (): HTMLVideoElement => {
    return this.video;
  };

  public getVolume = (): number => {
    return this.video.volume;
  };

  public isMute = (): boolean => {
    return this.video.muted;
  };

  public isPlaying = (): boolean => {
    return !this.video.paused;
  };

  public isLooping = (): boolean => {
    return this.video.loop;
  };

  public destroy = (): void => {
    this.removeAllListeners();
    this.removeListeners();
    setTimeout(() => { this.video.remove(); });
  };

  private addListeners = (): void => {
    this.video.addEventListener(Video.EVENT.ABORT, this.handleEvent);
    this.video.addEventListener(Video.EVENT.CAN_PLAY, this.handleEvent);
    this.video.addEventListener(Video.EVENT.CAN_PLAY_THROUGH, this.handleEvent);
    this.video.addEventListener(Video.EVENT.DURATION_CHANGE, this.handleEvent);
    this.video.addEventListener(Video.EVENT.EMPTIED, this.handleEvent);
    this.video.addEventListener(Video.EVENT.ENDED, this.handleEvent);
    this.video.addEventListener(Video.EVENT.ERROR, this.handleEvent);
    this.video.addEventListener(Video.EVENT.LOADED_DATA, this.handleEvent);
    this.video.addEventListener(Video.EVENT.LOADED_META_DATA, this.handleEvent);
    this.video.addEventListener(Video.EVENT.LOAD_START, this.handleEvent);
    this.video.addEventListener(Video.EVENT.PAUSE, this.handleEvent);
    this.video.addEventListener(Video.EVENT.PLAY, this.handleEvent);
    this.video.addEventListener(Video.EVENT.PLAYING, this.handleEvent);
    this.video.addEventListener(Video.EVENT.PROGRESS, this.handleEvent);
    this.video.addEventListener(Video.EVENT.RATE_CHANGE, this.handleEvent);
    this.video.addEventListener(Video.EVENT.SEEKED, this.handleEvent);
    this.video.addEventListener(Video.EVENT.SEEKING, this.handleEvent);
    this.video.addEventListener(Video.EVENT.STALLED, this.handleEvent);
    this.video.addEventListener(Video.EVENT.SUSPEND, this.handleEvent);
    this.video.addEventListener(Video.EVENT.TIME_UPDATE, this.handleEvent);
    this.video.addEventListener(Video.EVENT.VOLUME_CHANGE, this.handleEvent);
    this.video.addEventListener(Video.EVENT.WAITING, this.handleEvent);
  };

  private removeListeners = (): void => {
    this.video.removeEventListener(Video.EVENT.ABORT, this.handleEvent);
    this.video.removeEventListener(Video.EVENT.CAN_PLAY, this.handleEvent);
    this.video.removeEventListener(Video.EVENT.CAN_PLAY_THROUGH, this.handleEvent);
    this.video.removeEventListener(Video.EVENT.DURATION_CHANGE, this.handleEvent);
    this.video.removeEventListener(Video.EVENT.EMPTIED, this.handleEvent);
    this.video.removeEventListener(Video.EVENT.ENDED, this.handleEvent);
    this.video.removeEventListener(Video.EVENT.ERROR, this.handleEvent);
    this.video.removeEventListener(Video.EVENT.LOADED_DATA, this.handleEvent);
    this.video.removeEventListener(Video.EVENT.LOADED_META_DATA, this.handleEvent);
    this.video.removeEventListener(Video.EVENT.LOAD_START, this.handleEvent);
    this.video.removeEventListener(Video.EVENT.PAUSE, this.handleEvent);
    this.video.removeEventListener(Video.EVENT.PLAY, this.handleEvent);
    this.video.removeEventListener(Video.EVENT.PLAYING, this.handleEvent);
    this.video.removeEventListener(Video.EVENT.PROGRESS, this.handleEvent);
    this.video.removeEventListener(Video.EVENT.RATE_CHANGE, this.handleEvent);
    this.video.removeEventListener(Video.EVENT.SEEKED, this.handleEvent);
    this.video.removeEventListener(Video.EVENT.SEEKING, this.handleEvent);
    this.video.removeEventListener(Video.EVENT.STALLED, this.handleEvent);
    this.video.removeEventListener(Video.EVENT.SUSPEND, this.handleEvent);
    this.video.removeEventListener(Video.EVENT.TIME_UPDATE, this.handleEvent);
    this.video.removeEventListener(Video.EVENT.VOLUME_CHANGE, this.handleEvent);
    this.video.removeEventListener(Video.EVENT.WAITING, this.handleEvent);
  };

  private isVideoDrawn = (video: HTMLVideoElement): boolean => {
    const container = (document.querySelector(`[data-type="canvas-temp-container"][data-relation="${this.id}"]`)
      || document.createElement('div')) as HTMLDivElement;
    if (!container.hasAttribute('data-type')) {
      container.setAttribute('data-type', 'canvas-temp-container');
      container.setAttribute('data-relation', `${this.id}`);
      container.style.display = 'none';
      document.body.appendChild(container);
    }
    const canvasBuffer = (container.querySelector(`[data-type="canvas-buffer"][data-relation="${this.id}"]`)
      || document.createElement('canvas')) as HTMLCanvasElement;
    if (!canvasBuffer.hasAttribute('data-type')) {
      canvasBuffer.setAttribute('data-type', 'canvas-buffer');
      canvasBuffer.setAttribute('data-relation', `${this.id}`);
      canvasBuffer.width = video.videoWidth;
      canvasBuffer.height = video.videoHeight;
      container.appendChild(canvasBuffer);
    }
    const ctx: CanvasRenderingContext2D | null = canvasBuffer.getContext('2d');
    if (!ctx) {
      throw new Error('Error create canvas');
    }
    ctx.drawImage(video, 0, 0);
    const { data } = ctx.getImageData(0, 0, 1, 1);
    const [, , , a] = data;
    return a !== 0;
  };

  private tickVisualChecking = () => {
    let isVideoDrawn = false;
    try {
      isVideoDrawn = this.isVideoDrawn(this.video);
    } catch (error) {
      this.handleEvent({ type: Video.EVENT.CAN_PLAY_THROUGH_FIRST_FRAME_READY } as Event);
      return;
    }
    if (!isVideoDrawn) {
      setTimeout(this.tickVisualChecking, 50);
      return;
    }
    this.handleEvent({ type: Video.EVENT.CAN_PLAY_THROUGH_FIRST_FRAME_READY } as Event);
  };

  private tickLoadingTimeout = () => {
    this.handleEvent({ type: Video.EVENT.ERROR } as Event);
  };

  private handleEvent = (event: Event) => {
    this.dispatchEvent(event.type, event);
    if (event.type === Video.EVENT.CAN_PLAY_THROUGH_FIRST_FRAME_READY) {
      this.loadingTimeout = setTimeout(this.tickLoadingTimeout, this.loadForceIn);
    }
    if (event.type === Video.EVENT.PROGRESS && this.loadingTimeout) {
      clearTimeout(this.loadingTimeout);
      this.loadingTimeout = null;
    }
    if (event.type === Video.EVENT.CAN_PLAY_THROUGH) {
      if (this.loadingTimeout) {
        clearTimeout(this.loadingTimeout);
        this.loadingTimeout = null;
      }
      this.tickVisualChecking();
    }
  };
}

export default Video;
